// iter-274 (Feb 2026) — Main + Sub service picker for service cards.
// Consumed by ServiceCatalogPage / VisaIdServices / TrafficServices /
// DocumentAuthServices — replaces the ad-hoc "name_ar/name_en" inputs
// with two chained dropdowns fed by /api/admin/service-catalog/customer.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Info } from 'lucide-react';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {object}  p
 * @param {'invoice'|'customer'} p.scope   – which catalog to read
 * @param {string?} p.mainId               – controlled main service id
 * @param {string?} p.subId                – controlled sub service id
 * @param {string?} p.platform             – used only when scope==='invoice'
 * @param {function} p.onChange            – ({mainId, subId, mainAr, mainEn, subAr, subEn}) => void
 * @param {boolean?} p.dense               – smaller layout for modals
 * @param {string?}  p.testIdPrefix
 */
export function MainSubServicePicker({
  scope = 'customer', mainId, subId, platform, onChange,
  dense = false, testIdPrefix = 'mainsub-picker',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const url = new URL(`${API_URL}/api/admin/service-catalog/${scope}`);
        if (scope === 'invoice' && platform) url.searchParams.set('platform', platform);
        const r = await axios.get(url.toString(), { headers: getAuthHeaders() });
        if (!alive) return;
        setRows(r.data?.data || []);
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.data?.detail || e.message);
      } finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, [scope, platform]);

  const main = useMemo(() => rows.find(r => r.id === mainId) || null, [rows, mainId]);
  const subs = main?.sub_services || [];

  const emit = (next) => {
    const nextMain = next.mainId != null ? rows.find(r => r.id === next.mainId) : main;
    const nextSubId = next.subId != null ? next.subId : subId;
    const nextSub = nextMain?.sub_services?.find(s => s.id === nextSubId) || null;
    onChange?.({
      mainId: nextMain?.id || '',
      subId:  nextSub?.id  || '',
      mainAr: nextMain?.name_ar || '',
      mainEn: nextMain?.name_en || '',
      subAr:  nextSub?.name_ar  || '',
      subEn:  nextSub?.name_en  || '',
    });
  };

  const changeMain = (id) => emit({ mainId: id, subId: '' });
  const changeSub  = (id) => emit({ subId: id });

  const inputCls = dense
    ? 'w-full h-9 rounded-md bg-[#050505] border border-white/10 text-white px-3 text-sm'
    : 'w-full h-11 rounded-md bg-[#050505] border border-white/10 text-white px-3';

  if (err) {
    return (
      <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex gap-2">
        <Info className="w-4 h-4" /> {err}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <label className="text-white/60 text-xs mb-1 block">اسم الخدمة الرئيسية · Main service</label>
        <select value={mainId || ''} onChange={(e) => changeMain(e.target.value)}
                disabled={loading || !rows.length} className={inputCls}
                data-testid={`${testIdPrefix}-main`}>
          <option value="">{loading ? 'جارِ التحميل…' : (rows.length ? '— اختر —' : 'لا توجد خدمات')}</option>
          {rows.map(r => (
            <option key={r.id} value={r.id}>
              {r.name_ar}{r.name_en ? ` · ${r.name_en}` : ''}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-white/60 text-xs mb-1 block">اسم الخدمة الفرعية · Sub service</label>
        <select value={subId || ''} onChange={(e) => changeSub(e.target.value)}
                disabled={!main} className={inputCls}
                data-testid={`${testIdPrefix}-sub`}>
          <option value="">{main ? '— اختر —' : 'اختر الرئيسية أولاً'}</option>
          {subs.map(s => (
            <option key={s.id} value={s.id}>
              {s.name_ar}{s.name_en ? ` · ${s.name_en}` : ''}
            </option>
          ))}
        </select>
      </div>
      {loading && (
        <div className="col-span-full text-xs text-white/40 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> تحميل القوائم من /admin/service-catalog/{scope}
        </div>
      )}
    </div>
  );
}

export default MainSubServicePicker;
